import { uploadOfflineImageAttachments, filterRemoteUrls } from '../uploadAttachments';
import { deepCopy } from '../utils';

const isRemoteUrl = (url) => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

/**
 * Offline operation config for updateTicketPhotos mutation.
 *
 * Handles:
 * - Queuing ticket photo updates when offline
 * - Storing local image URIs as attachments
 * - Uploading images when back online before updating ticket
 * - Exposing queued photos through operation metadata for ticket previews
 */
const updateTicketPhotosConfig = {
	queueOnNetworkError: true,
	queueWhenAttachments: true,
	serializeVariables: ({ variables }) => deepCopy(variables),
	extractAttachments: ({ variables }) => {
		const imageUrls = variables?.imageUrls || [];

		return imageUrls
			.filter(url => typeof url === 'string' && !isRemoteUrl(url))
			.map((url, index) => ({
				type: 'image',
				localUri: url,
				index,
			}));
	},
	createMeta: async ({ variables, helpers }) => ({
		ticketId: variables?.id,
		type: 'updateTicketPhotos',
		localImageUrls: (variables?.imageUrls || []).filter(url => typeof url === 'string' && !isRemoteUrl(url)),
		s3Path: 'images/machines/',
		serverEndpoint: helpers?.getServerEndpoint ? await helpers.getServerEndpoint() : null,
	}),
	prepareVariables: async ({ entry }) => {
		const clonedVariables = deepCopy(entry.variables);
		const attachments = entry.attachments || [];

		if (!attachments.length) {
			clonedVariables.imageUrls = filterRemoteUrls(clonedVariables.imageUrls);
			return clonedVariables;
		}

		const uploaded = await uploadOfflineImageAttachments({ attachments, meta: entry.meta });
		const existingRemote = filterRemoteUrls(clonedVariables.imageUrls);
		const uploadedUrls = uploaded.map(file => file.remoteUrl);

		clonedVariables.imageUrls = [...existingRemote, ...uploadedUrls];
		return clonedVariables;
	},
	getOfflineResult: ({ variables }) => ({
		updateTicketPhotos: {
			__typename: 'Ticket',
			id: variables.id,
			imageUrls: variables.imageUrls || [],
		},
	}),
};

export default updateTicketPhotosConfig;
